import React, { useEffect, useState } from 'react';
import { XIcon, CameraIcon, LockIcon, KeyIcon, ChevronDownIcon } from 'lucide-react';
import { useUser, UserRole } from '../contexts/UserContext';
interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function EditProfileModal({
  isOpen,
  onClose
}: EditProfileModalProps) {
  const {
    role: globalRole,
    setRole: setGlobalRole,
    name: globalName,
    setName: setGlobalName,
    email: globalEmail,
    setEmail: setGlobalEmail
  } = useUser();
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [name, setName] = useState(globalName);
  const [email, setEmail] = useState(globalEmail);
  const [role, setRole] = useState<UserRole>(globalRole);
  const [country, setCountry] = useState('México');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowPasswordSection(false);
      setName(globalName);
      setEmail(globalEmail);
      setRole(globalRole);
      setCountry('México');
    }
  }, [isOpen, globalName, globalEmail, globalRole]);
  if (!isOpen) return null;
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia'];
  const roles = [{
    value: 'authenticated-public-user',
    label: 'Authenticated Public User'
  }, {
    value: 'customer',
    label: 'Customer'
  }, {
    value: 'dealer',
    label: 'Dealer'
  }, {
    value: 'sales-manager',
    label: 'Sales Manager'
  }, {
    value: 'operations-manager',
    label: 'Operations Manager'
  }, {
    value: 'manager-business-unit',
    label: 'Manager Business Unit'
  }, {
    value: 'ceo',
    label: 'CEO'
  }, {
    value: 'logistic',
    label: 'Logistic'
  }, {
    value: 'marketing',
    label: 'Marketing'
  }, {
    value: 'financial-manager',
    label: 'Financial Manager'
  }, {
    value: 'system-admin',
    label: 'System Admin'
  }, {
    value: 'dealer-admin',
    label: 'Dealer Admin'
  }, {
    value: 'region-admin',
    label: 'Region Admin'
  }];
  return <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={handleBackdropClick} data-id="element-3314" data-mp="hwt055ffquc7">
      <div className="bg-[#0f1d3a] rounded-2xl shadow-2xl border border-white/10 w-full max-w-lg my-8 relative flex flex-col max-h-[90vh]" data-id="element-3315" data-mp="czavhr8gb1a0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0" data-id="element-3316" data-mp="3gf89kb9qgtdm">
          <h2 className="text-white font-semibold text-lg" data-id="element-3317" data-mp="3uf9b7sxto4u5">Edit Profile</h2>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" data-id="element-3318" data-mp="3qbuq4nv10y9u">
            <XIcon size={20} data-id="element-3319" data-mp="19lnosljtel9y" />
          </button>
        </div>

        {/* Content (Scrollable) */}
        <div className="p-6 overflow-y-auto flex-1 no-scrollbar" data-id="element-3320" data-mp="15llx957sn88e">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-8" data-id="element-3321" data-mp="1d43bfoqm7b7p">
            <div className="relative" data-id="element-3322" data-mp="1i4r92t9cmqaw">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-4 ring-[#0f1d3a] shadow-lg" data-id="element-3323" data-mp="1i5sof9xedyyf">
                <span className="text-2xl font-bold text-white" data-id="element-3324" data-mp="187ct0urc83j4">DA</span>
              </div>
              <button className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-500 text-white rounded-full p-1.5 shadow-lg border-2 border-[#0f1d3a] transition-colors" title="Change photo" data-id="element-3325" data-mp="1vvmetwojwra9">
                <CameraIcon size={14} data-id="element-3326" data-mp="1m6uo1rv88d0x" />
              </button>
            </div>
            <span className="text-blue-400 text-xs mt-2 font-medium cursor-pointer hover:text-blue-300 transition-colors" data-id="element-3327" data-mp="1r4w7wf0h8gdg">
              Change photo
            </span>
            <span className="inline-flex items-center mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/20 text-teal-300 border border-teal-500/30" data-id="element-3328" data-mp="2esnjfge70pdo">
              Dealer
            </span>
          </div>

          {/* Form Fields */}
          <div className="space-y-5" data-id="element-3329" data-mp="qxspdelnyq6p">
            <div data-id="element-3330" data-mp="qwr46haxonb9">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3331" data-mp="r0ew5c0qd1nv">
                Full name
              </label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Your name" data-id="element-3332" data-mp="h0e8xezqq1ee" />
            </div>

            <div data-id="element-3333" data-mp="14wilos0nslu0">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3334" data-mp="14w0l5j6yrlga">
                Email
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="your@email.com" data-id="element-3335" data-mp="1xe4fcsec11r7" />
            </div>

            <div data-id="element-3336" data-mp="24you2pvxmg9h">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3337" data-mp="24y5lw0w3368n">
                Role
              </label>
              <div className="relative" data-id="element-3338" data-mp="29ytcwmkbbn2v">
                <select value={role} onChange={e => setRole(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3339" data-mp="2ew39qna1710q">
                  {roles.map(r => <option key={r.value} value={r.value} className="bg-slate-800 text-white" data-id="element-3340" data-mp="2snhpml8n6m3k">
                      {r.label}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3341" data-mp="32le2bhh89p3s">
                  <ChevronDownIcon size={16} data-id="element-3342" data-mp="37jz3pk87uett" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-3343" data-mp="2hvtoyoxwj2h3">
              <div data-id="element-3344" data-mp="2hu0d8v8fls28">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3345" data-mp="2cxib834hk7tl">
                  Country
                </label>
                <div className="relative" data-id="element-3346" data-mp="2rmy3jnjo0lbe">
                  <select value={country} onChange={e => setCountry(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3347" data-mp="2rnzhbzs2flwt">
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3348" data-mp="2vq490syye8gt">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3349" data-mp="35mh1jnirig9z">
                    <ChevronDownIcon size={16} data-id="element-3350" data-mp="1g8uaqkotd37x" />
                  </div>
                </div>
              </div>

              <div data-id="element-3351" data-mp="3mv9qacfrvqp5">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3352" data-mp="3muqb33hibget">
                  Region
                </label>
                <div className="relative" data-id="element-3353" data-mp="3rtteyklr3gl7">
                  <input type="text" value="Aisco America" readOnly className="w-full bg-white/5 border border-white/5 rounded-lg pl-10 pr-4 py-2.5 text-blue-300/70 cursor-not-allowed focus:outline-none" data-id="element-3354" data-mp="3d5gf3cjjldlf" />
                  <LockIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400/50" data-id="element-3355" data-mp="3450fwf9u0smd" />
                </div>
              </div>
            </div>

            {/* Change Password Section */}
            <div className="pt-4 border-t border-white/10 mt-6" data-id="element-3356" data-mp="2u7dynm528oz8">
              <button type="button" onClick={() => setShowPasswordSection(!showPasswordSection)} className="flex items-center justify-between w-full text-left group" data-id="element-3357" data-mp="2z5nu7xup5rld">
                <div className="flex items-center text-blue-400 group-hover:text-blue-300 transition-colors" data-id="element-3358" data-mp="sg0tqx33eh36">
                  <KeyIcon size={18} className="mr-2" data-id="element-3359" data-mp="xc818ub500n8" />
                  <span className="font-medium text-sm" data-id="element-3360" data-mp="3gbsiv39zgfq1">Change password</span>
                </div>
                <ChevronDownIcon size={18} className={`text-blue-400 group-hover:text-blue-300 transition-transform duration-200 ${showPasswordSection ? 'rotate-180' : ''}`} data-id="element-3361" data-mp="3ccbc86itlcja" />
              </button>

              {showPasswordSection && <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-3362" data-mp="32kxh0axwiyvy">
                  <div data-id="element-3363" data-mp="2xnxay011xw4u">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3364" data-mp="2xjzqnb2qmyjd">
                      New password
                    </label>
                    <input type="password" className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="••••••••" data-id="element-3365" data-mp="2ompcnhyjcvar" />
                  </div>
                  <div data-id="element-3366" data-mp="huydqersxdzo">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3367" data-mp="hvrcotkiqz79">
                      Confirm password
                    </label>
                    <input type="password" className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="••••••••" data-id="element-3368" data-mp="udk5ogk5bxli" />
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end space-x-3 flex-shrink-0 bg-black/20 rounded-b-2xl" data-id="element-3369" data-mp="bey6x8c3qikr">
          <button onClick={onClose} className="bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors" data-id="element-3370" data-mp="gczotpxskbun">
            Cancel
          </button>
          <button onClick={() => {
          setGlobalRole(role);
          setGlobalName(name);
          setGlobalEmail(email);
          onClose();
        }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-3371" data-mp="1rtzf6g0kwgm1">
            Save changes
          </button>
        </div>
      </div>
    </div>;
}